// 8 方位のリサイズハンドル用ドラッグハンドラ。
//
// 振る舞い:
// - pointerdown でドラッグ開始（基準位置を捕捉）
// - pointermove でステージ座標差分を方位に応じて x/y/w/h に反映し、対象 DOM を
//   即時プレビュー（doc store/サーバへは送らない）
// - pointerup で最終 x/y/w/h を transformObject op として送信し永続化
//
// 回転対応:
// - object.rotation を受け取り、ポインタ移動量を逆回転 (-θ) して
//   object-local 軸基準のリサイズへ変換する。これにより、回転後の図形でも
//   east ハンドルなら横方向のみが伸び縦方向は変化しない（縦横が連動するバグの修正）。
// - リサイズに伴うローカル中心移動量を世界座標へ +θ で戻して新しい AABB の x/y を求める。
//
// 制約:
// - 最小 16px 制限（潰れ防止）
// - Shift キー押下中は、四隅ハンドル（nw / ne / se / sw）のみアスペクト比を初期値で固定する。
//   辺の中央ハンドル（n / s / e / w）は片軸のみのリサイズなので Shift は無視する。

import { useEffect, type RefObject } from "react";
import { clampFrameToCanvas } from "@refinement-canvas/domain";
import { CANVAS_SNAP_PX } from "../../../lib/canvas-coordinates";
import { useCanvasStore } from "../store/canvas-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

export type ResizeDirection = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

interface Options {
  readonly direction: ResizeDirection;
  readonly handleRef: RefObject<HTMLElement | null>;
  readonly objectId: string;
  readonly dispatcher: CanvasOpDispatcher;
  readonly initialX: number;
  readonly initialY: number;
  readonly initialW: number;
  readonly initialH: number;
  /** 対象オブジェクトの回転角（度）。0 または未指定なら従来通りワールド軸の挙動。 */
  readonly initialRotation?: number | undefined;
  /** 対象オブジェクトの DOM 要素（プレビュー用に left/top/width/height を直接書き換える）。 */
  readonly targetRef: RefObject<HTMLElement | null>;
}

const MIN_SIZE_PX = 16;
// 他参加者向けゴースト中継の最小送信間隔（ms）。約 20Hz に間引いて通信量を抑える。
const TRANSIENT_THROTTLE_MS = 50;

export function useResizeHandle(options: Options): void {
  const {
    direction,
    handleRef,
    objectId,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    initialRotation,
    targetRef,
  } = options;

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return undefined;

    let dragging: {
      startClientX: number;
      startClientY: number;
      shiftKey: boolean;
      // 最後の pointermove プレビューで確定した枠。pointerup ではこれをそのまま採用し、
      // 「見た目（最後のプレビュー）」と「確定値」を完全一致させる。
      // event 座標と event.shiftKey から再計算すると、リリース直前の Shift 解除や僅かな
      // 座標変化でアスペクト比固定の主従が反転し、確定値だけずれる恐れがあるため枠を保持する。
      lastPreviewFrame: { x: number; y: number; w: number; h: number } | null;
      // 他参加者向けゴースト中継を間引くための直近送信時刻（ms）。
      lastTransientAt: number;
    } | null = null;

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      dragging = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        shiftKey: event.shiftKey,
        lastPreviewFrame: null,
        lastTransientAt: 0,
      };
      handle.setPointerCapture(event.pointerId);
      // 親オブジェクトの drag や stage の pan を起こさせない
      event.stopPropagation();
    };

    const computeResized = (
      clientX: number,
      clientY: number,
      shiftKey: boolean
    ): { x: number; y: number; w: number; h: number } => {
      const view = useCanvasStore.getState().view;
      const worldDx = (clientX - (dragging?.startClientX ?? clientX)) / view.zoom;
      const worldDy = (clientY - (dragging?.startClientY ?? clientY)) / view.zoom;

      // 方位ごとに左/右/上/下のどれを動かすかを決める
      const movesLeft = direction === "nw" || direction === "w" || direction === "sw";
      const movesRight = direction === "ne" || direction === "e" || direction === "se";
      const movesTop = direction === "nw" || direction === "n" || direction === "ne";
      const movesBottom = direction === "sw" || direction === "s" || direction === "se";

      // 世界座標のポインタ移動量をオブジェクト・ローカル軸へ写像する。
      // 回転 θ で描画されている図形に対し、ローカル軸はワールド軸を -θ 回転したもの。
      const rotationDeg = initialRotation ?? 0;
      const rad = (rotationDeg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      let localDx = worldDx * cos + worldDy * sin;
      let localDy = -worldDx * sin + worldDy * cos;

      // Shift 押下中、四隅ハンドルのみ初期アスペクト比 (w/h) を維持するようローカル差分を補正する。
      // ローカル軸上で対角方向への移動量を、初期 w/h 比に従って w または h どちらが
      // より大きく動こうとしているかで主導軸を決め、もう一方を比率で連動させる。
      const isCorner =
        direction === "nw" || direction === "ne" || direction === "se" || direction === "sw";
      if (shiftKey && isCorner && initialW > 0 && initialH > 0) {
        const aspect = initialW / initialH;
        // 四隅の方位ごとに、w/h を増やす方向を符号として整える。
        const signX = direction === "ne" || direction === "se" ? 1 : -1;
        const signY = direction === "sw" || direction === "se" ? 1 : -1;
        const desiredDw = signX * localDx;
        const desiredDh = signY * localDy;
        // 主導軸の絶対値が大きい方に追随させる（w が動く量に合わせて h を比率で揃える）。
        if (Math.abs(desiredDw) >= Math.abs(desiredDh) * aspect) {
          const adjustedDh = desiredDw / aspect;
          localDy = signY * adjustedDh;
        } else {
          const adjustedDw = desiredDh * aspect;
          localDx = signX * adjustedDw;
        }
      }

      let nextW = initialW;
      let nextH = initialH;
      let centerLocalShiftX = 0;
      let centerLocalShiftY = 0;

      // 横方向: w を変化させ、ローカル X 上で中心が effectiveLocalDx/2 ずれる。
      if (movesLeft) {
        let effectiveLocalDx = localDx;
        let candidateW = initialW - effectiveLocalDx;
        if (candidateW < MIN_SIZE_PX) {
          effectiveLocalDx = initialW - MIN_SIZE_PX;
          candidateW = MIN_SIZE_PX;
        }
        nextW = candidateW;
        centerLocalShiftX = effectiveLocalDx / 2;
      } else if (movesRight) {
        let effectiveLocalDx = localDx;
        let candidateW = initialW + effectiveLocalDx;
        if (candidateW < MIN_SIZE_PX) {
          effectiveLocalDx = MIN_SIZE_PX - initialW;
          candidateW = MIN_SIZE_PX;
        }
        nextW = candidateW;
        centerLocalShiftX = effectiveLocalDx / 2;
      }

      // 縦方向も同様にローカル Y 上で計算する。
      if (movesTop) {
        let effectiveLocalDy = localDy;
        let candidateH = initialH - effectiveLocalDy;
        if (candidateH < MIN_SIZE_PX) {
          effectiveLocalDy = initialH - MIN_SIZE_PX;
          candidateH = MIN_SIZE_PX;
        }
        nextH = candidateH;
        centerLocalShiftY = effectiveLocalDy / 2;
      } else if (movesBottom) {
        let effectiveLocalDy = localDy;
        let candidateH = initialH + effectiveLocalDy;
        if (candidateH < MIN_SIZE_PX) {
          effectiveLocalDy = MIN_SIZE_PX - initialH;
          candidateH = MIN_SIZE_PX;
        }
        nextH = candidateH;
        centerLocalShiftY = effectiveLocalDy / 2;
      }

      // ローカル中心移動量を世界座標へ戻し、AABB の左上座標 (x,y) を更新する。
      const worldCenterShiftX = centerLocalShiftX * cos - centerLocalShiftY * sin;
      const worldCenterShiftY = centerLocalShiftX * sin + centerLocalShiftY * cos;
      const originalCenterX = initialX + initialW / 2;
      const originalCenterY = initialY + initialH / 2;
      const newCenterX = originalCenterX + worldCenterShiftX;
      const newCenterY = originalCenterY + worldCenterShiftY;
      const nextX = newCenterX - nextW / 2;
      const nextY = newCenterY - nextH / 2;

      // リサイズ中もキャンバス境界を越えないよう、最終フレームを必ず有効範囲へ丸める。
      return clampFrameToCanvas({ x: nextX, y: nextY, w: nextW, h: nextH });
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (!dragging) return;
      // ドラッグ中の Shift 切り替えにも追従できるよう、最新のキー状態で再計算する。
      const next = computeResized(event.clientX, event.clientY, event.shiftKey);
      // 確定時に同じ枠を使うため、最後に計算したプレビュー枠を保持する。
      dragging.lastPreviewFrame = next;
      const target = targetRef.current;
      if (target) {
        target.style.left = `${next.x}px`;
        target.style.top = `${next.y}px`;
        target.style.width = `${next.w}px`;
        target.style.height = `${next.h}px`;
      }
      updateSelectionBoxPreview(next);

      // 他参加者へリサイズ中ゴーストを中継する（一定間隔で間引く）。
      const now = Date.now();
      if (now - dragging.lastTransientAt >= TRANSIENT_THROTTLE_MS) {
        dragging.lastTransientAt = now;
        dispatcher.previewTransforms([
          { objectId, transform: { x: next.x, y: next.y, w: next.w, h: next.h } },
        ]);
      }
    };

    const onPointerUp = (event: PointerEvent): void => {
      if (!dragging) return;
      // 最後の pointermove プレビュー枠をそのまま確定に使う。プレビューが一度も走っていない
      // （pointerdown 直後に pointerup）場合のみ、現在のキー状態で計算した枠へフォールバックする。
      const next =
        dragging.lastPreviewFrame ??
        computeResized(event.clientX, event.clientY, event.shiftKey);
      dragging = null;
      if (handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
      // グリッド吸着 ON のとき、最終 x/y/w/h を CANVAS_SNAP_PX 単位で丸める。
      const { gridSnapEnabled } = useCanvasStore.getState();
      const finalize = gridSnapEnabled
        ? clampFrameToCanvas({
            x: snapToGrid(next.x),
            y: snapToGrid(next.y),
            w: Math.max(MIN_SIZE_PX, snapToGrid(next.w)),
            h: Math.max(MIN_SIZE_PX, snapToGrid(next.h)),
          })
        : next;
      if (gridSnapEnabled) {
        const target = targetRef.current;
        if (target) {
          target.style.left = `${finalize.x}px`;
          target.style.top = `${finalize.y}px`;
          target.style.width = `${finalize.w}px`;
          target.style.height = `${finalize.h}px`;
        }
        updateSelectionBoxPreview(finalize);
      }
      dispatcher.transformObject(objectId, finalize);
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerUp);
    };
  }, [
    direction,
    handleRef,
    objectId,
    dispatcher,
    initialX,
    initialY,
    initialW,
    initialH,
    initialRotation,
    targetRef,
  ]);
}

function updateSelectionBoxPreview(next: {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}): void {
  const box = document.querySelector<HTMLElement>('[data-testid="canvas-selection-box"]');
  if (!box) return;
  box.style.left = `${next.x}px`;
  box.style.top = `${next.y}px`;
  box.style.width = `${next.w}px`;
  box.style.height = `${next.h}px`;
}

function snapToGrid(value: number): number {
  return Math.round(value / CANVAS_SNAP_PX) * CANVAS_SNAP_PX;
}
